import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const ShippingDeliveryPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Service Delivery Policy"
        description="Learn about our service delivery policies for EmailValidator. Understand how our email validation services are delivered and what to expect from our platform."
        keywords="service delivery policy, email validation delivery, service terms, delivery policy, email validator service delivery"
        canonical="https://mailvalidator.octalchip.com/shipping-delivery"
      />
      
      <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4">
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Delivery Policy</h1>
            <p className="text-gray-600">Last updated: December 17, 2024</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <section id="service-delivery-overview">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Service Delivery Overview</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                EmailValidator is a Software-as-a-Service (SaaS) platform that provides email validation services through our web application and API. Since we deliver digital services, there is no physical shipping involved.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Service Access:</strong> Upon successful subscription or registration, you will have immediate access to our email validation services through our web platform and API endpoints.
              </p>
            </section>

            <section id="account-activation">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Account Activation and Access</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Free Plan Activation:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Immediate access upon account registration</li>
                <li>100 email validations per month included</li>
                <li>Basic validation features available</li>
                <li>No credit card required</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3 mt-4">
                <strong>Paid Plan Activation:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Access granted immediately after successful payment</li>
                <li>Full plan features unlocked instantly</li>
                <li>Credits allocated based on your plan</li>
                <li>Welcome email with account details sent</li>
              </ul>
            </section>

            <section id="service-availability-delivery">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Service Availability</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Uptime Commitment:</strong> We strive to maintain 99.9% uptime for our email validation services. Our platform is designed to be available 24/7 for your validation needs.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Maintenance Windows:</strong> Scheduled maintenance is typically performed during low-traffic hours (2:00 AM - 6:00 AM IST) and advance notice is provided via email and our status page.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Emergency Maintenance:</strong> In case of critical security updates or emergency fixes, we may perform unscheduled maintenance with immediate notification to affected users.
              </p>
            </section>

            <section id="api-service-delivery">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. API Service Delivery</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>API Access:</strong> Paid plan subscribers receive API access credentials immediately after payment confirmation.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>API Documentation:</strong> Comprehensive API documentation is available at{' '}
                <Link to="/docs" className="text-primary hover:underline">
                  /docs
                </Link>
                {' '}with detailed integration guides and code examples.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Response Times:</strong> Our API typically responds within 100-500ms for single email validations and within 1-5 seconds for bulk validations, depending on the size of the request.
              </p>
            </section>

            <section id="data-processing-delivery">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Processing and Delivery</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Real-Time Processing:</strong> Email validations are processed in real-time, with results delivered immediately upon completion.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Bulk Processing:</strong> For bulk email validations:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Files up to 10,000 emails: Processed within 5-10 minutes</li>
                <li>Files up to 100,000 emails: Processed within 30-60 minutes</li>
                <li>Larger files: Processing time varies based on size and server load</li>
                <li>Progress tracking available for all bulk operations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3 mt-4">
                <strong>Result Delivery:</strong> Validation results are delivered through:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Web interface with real-time updates</li>
                <li>Downloadable CSV/JSON files</li>
                <li>API responses for programmatic access</li>
                <li>Email notifications for completed bulk operations</li>
              </ul>
            </section>

            <section id="service-quality-support">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Service Quality and Support</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Quality Assurance:</strong> Our email validation services undergo continuous quality monitoring and improvement to ensure accurate results.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Support Delivery:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Free Plan: Community support and documentation</li>
                <li>Pro Plan: Priority email support with 24-hour response time</li>
                <li>Enterprise Plan: Dedicated support with 4-hour response time and phone support</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3 mt-4">
                <strong>Support Channels:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Email: support@emailvalidator.com</li>
                <li>Phone: +91 7574082582 (Enterprise customers)</li>
                <li>Documentation: Comprehensive guides and tutorials</li>
                <li>Status Page: Real-time service status updates</li>
              </ul>
            </section>

            <section id="service-limitations">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Service Limitations</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Rate Limits:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Free Plan: 100 validations per month</li>
                <li>Pro Plan: 10,000 validations per month</li>
                <li>Enterprise Plan: Unlimited validations</li>
                <li>API Rate Limits: 100 requests per minute per API key</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-3 mt-4">
                <strong>File Size Limits:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Bulk upload: Maximum 50MB per file</li>
                <li>Supported formats: CSV, TXT, XLSX</li>
                <li>Maximum emails per file: 1,000,000</li>
              </ul>
            </section>

            <section id="service-updates">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Service Updates and Improvements</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Continuous Improvement:</strong> We regularly update our validation algorithms and add new features to improve service quality and user experience.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Feature Updates:</strong> New features and improvements are automatically available to all users without additional delivery time or setup required.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Notification:</strong> Major updates and new features are communicated through:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Email newsletters</li>
                <li>In-app notifications</li>
                <li>Blog posts and documentation updates</li>
                <li>Social media announcements</li>
              </ul>
            </section>

            <section id="delivery-contact">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                For questions about our service delivery or technical support:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>
                  <strong>General Support:</strong>{' '}
                  <a href="mailto:support@emailvalidator.com" className="text-primary hover:underline">
                    support@emailvalidator.com
                  </a>
                </li>
                <li>
                  <strong>Sales Inquiries:</strong>{' '}
                  <a href="mailto:business@octalchip.com" className="text-primary hover:underline">
                    business@octalchip.com
                  </a>
                </li>
                <li>
                  <strong>Phone:</strong>{' '}
                  <a href="tel:+917574082582" className="text-primary hover:underline">
                    +91 7574082582
                  </a>
                </li>
                <li>
                  <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM IST
                </li>
              </ul>
            </section>
          </div>
        </div>
        <Footer />
    </div>
  );
};

export default ShippingDeliveryPolicy; 